import { Chip, makeStyles } from '@material-ui/core';

import DeliveryNoteOrderRow from './DeliveryNoteOrderRow.component';
import DeliveryNoteHandwriteBoxes from './DeliveryNoteHandwriteBoxes.component';
import { DeliveryNote } from '../../types/delivery-note.type';
import pluralFormatter from '../../../shared/utils/plural-formater';
import { formatPhoneNumber } from '../../../shared/utils/format-phone-number.util';
import { formatDateToReadable } from '../../../shared/utils/format-date.util';
import { formatPrice } from '../../../shared/utils/format-price.util';

type StyleProps = {
  isPrintMode?: boolean;
};

const useStyles = makeStyles((theme) => ({
  root: {
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: '5px',
    marginBottom: '1rem',
  },

  header: ({ isPrintMode }: StyleProps) => ({
    display: 'flex',
    gap: '1rem',
    padding: '0.75rem 1rem',
    borderRadius: '5px 5px 0 0',
    backgroundColor: isPrintMode ? undefined : theme.palette.grey[100],

    '& p': {
      margin: '0 0 0.25rem 0',
    },

    '& > section:nth-child(1)': {
      flexBasis: '25%',
      borderRight: '1px solid rgba(0, 0, 0, 0.12)',
    },
    '& > section:nth-child(2)': {
      flexBasis: '60%',
      borderRight: '1px solid rgba(0, 0, 0, 0.12)',
    },
    '& > section:nth-child(3)': {
      flexBasis: '15%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'start',
      gap: '0.5rem',
    },
  }),

  chips: {
    borderRadius: '5px',

    '& > .MuiSvgIcon-root': {
      fill: theme.palette.admin.primary.contrastText,
    },
  },

  lines: {
    '& p': {
      margin: '0 0 0.25rem 0',
    },
  },
}));

type Props = {
  note: DeliveryNote;
  isPrintMode?: boolean;
};

function HeaderSection({ note, isPrintMode }: Props) {
  const classes = useStyles({ isPrintMode });

  return (
    <header className={classes.header}>
      <section>
        <Chip
          className={classes.chips}
          style={{ marginBottom: '5px' }}
          size="small"
          label={
            <b>
              {pluralFormatter({
                count: note.orders.length,
                singular: `${note.orders.length} commande`,
                plural: `${note.orders.length} commandes`,
              })}
            </b>
          }
        />
        <p>{`${note.city} - ${note.zip}`}</p>
        <p>{formatDateToReadable(note.deliveryDate)}</p>
        <p>{note.schedules.deposit}</p>
      </section>
      <section>
        <p>
          <b>Éco-acteur : </b>
          {note.title}
        </p>
        <p>
          <b>Adresse : </b>
          {`${note.address} - ${note.city} ${note.zip}`}
        </p>
        <p>
          <b>Téléphone : </b>
          {formatPhoneNumber(note.phone)}
        </p>
        {note.informations && (
          <p>
            <b>Informations : </b>
            {note.informations}
          </p>
        )}
        {note.additionalInfos && note.additionalInfos !== '' && (
          <p>
            <b>Complément : </b>
            {note.additionalInfos}
          </p>
        )}
      </section>
      <section>
        <Chip
          className={classes.chips}
          size="small"
          label={pluralFormatter({
            count: note.bagsCount,
            singular: `${note.bagsCount} sac`,
            plural: `${note.bagsCount} sacs`,
          })}
        />
        <Chip className={classes.chips} size="small" label={formatPrice(note.total)} />
        {isPrintMode && <DeliveryNoteHandwriteBoxes />}
      </section>
    </header>
  );
}

export default function EcoDeliveryNoteCard({ note, isPrintMode = false }: Props) {
  const classes = useStyles({ isPrintMode });

  return (
    <article className={classes.root}>
      <HeaderSection note={note} isPrintMode={isPrintMode} />

      {note.orders.map((order, orderIndex) => (
        <DeliveryNoteOrderRow key={order.id} order={order} index={orderIndex + 1} orderCount={note.orders.length} />
      ))}
    </article>
  );
}
